/**
 * Entscheidung über einen Vorschlag – oder eine Rückfrage dazu.
 *
 * Jede Statusänderung verlangt eine Begründung im selben Dialog: bei einer
 * Ablehnung Pflicht, sonst freiwillig mit sinnvoller Vorbelegung. Sie wird ein
 * Verlaufseintrag und geht gleichzeitig per Mail an den Ersteller.
 *
 * Der Grund dafür steht im Design: Ein Vorschlag ohne Antwort ist der häufigste
 * Grund, warum Mitarbeiter keine mehr einbringen.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiFehler } from '../api';
import { useKora } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import type { Vorschlag } from '../typen';

/** Vorbelegung: ein Satz, der zum Schritt passt und geändert werden darf. */
const VORBELEGUNG: Record<string, string> = {
  Genehmigt: 'Guter Vorschlag – wird eingeplant.',
  Umgesetzt: 'Ist umgesetzt und ab sofort verfügbar.',
  Abgelehnt: '',
  Rückfrage: '',
  Antwort: '',
};

const TITEL: Record<string, string> = {
  Genehmigt: 'Vorschlag genehmigen',
  Umgesetzt: 'Als umgesetzt melden',
  Abgelehnt: 'Vorschlag ablehnen',
  Rückfrage: 'Rückfrage stellen',
  Antwort: 'Auf die Rückfrage antworten',
};

export function EntscheidungModal({
  vorschlag,
  typ,
  fragesteller,
  onSchliessen,
}: {
  vorschlag: Vorschlag;
  typ: string;
  /** Wer die offene Rückfrage gestellt hat – nur beim Typ „Antwort" gesetzt. */
  fragesteller?: string;
  onSchliessen: () => void;
}) {
  const queryClient = useQueryClient();
  const { toastZeigen, nutzer } = useKora();
  const [text, setText] = useState(VORBELEGUNG[typ] ?? '');
  const [fehler, setFehler] = useState('');

  const pflicht = typ === 'Abgelehnt' || typ === 'Rückfrage' || typ === 'Antwort';

  // Wer seinen eigenen Vorschlag beantwortet, bekommt keine Mail an sich selbst.
  const eigener = vorschlag.ersteller === nutzer?.anzeigename;

  const speichern = useMutation({
    mutationFn: () =>
      api.sende(`/vorschlaege/${vorschlag.id}/eintrag`, { typ, text: text.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vorschlag', String(vorschlag.id)] });
      void queryClient.invalidateQueries({ queryKey: ['vorschlaege'] });
      void queryClient.invalidateQueries({ queryKey: ['vorschlaege-anzahl'] });
      if (typ === 'Antwort') {
        toastZeigen(
          fragesteller
            ? `${vorschlag.kennung}: Deine Antwort ist bei ${fragesteller}.`
            : `${vorschlag.kennung}: Antwort im Verlauf festgehalten.`,
        );
      } else {
        toastZeigen(
          eigener
            ? `${vorschlag.kennung} steht jetzt auf „${typ}".`
            : `${vorschlag.kennung}: ${vorschlag.ersteller} ist verständigt.`,
        );
      }
      onSchliessen();
    },
    onError: (f) =>
      setFehler(f instanceof ApiFehler ? f.message : 'Die Antwort konnte nicht gespeichert werden.'),
  });

  function absenden() {
    setFehler('');
    if (pflicht && !text.trim()) {
      if (typ === 'Abgelehnt') {
        setFehler('Eine Ablehnung braucht eine Begründung – sonst bringt niemand mehr etwas ein.');
      } else if (typ === 'Antwort') {
        setFehler('Bitte die Antwort eingeben.');
      } else {
        setFehler('Bitte die Rückfrage formulieren.');
      }
      return;
    }
    speichern.mutate();
  }

  return (
    <Modal
      titel={TITEL[typ] ?? typ}
      untertitel={`${vorschlag.kennung} – ${vorschlag.titel}`}
      breite={520}
      fest={speichern.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={speichern.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer
            onClick={absenden}
            ton={typ === 'Umgesetzt' || typ === 'Antwort' ? 'gruen' : 'rot'}
            disabled={speichern.isPending}
          >
            {speichern.isPending ? 'Wird gesendet …' : 'Absenden'}
          </KnopfPrimaer>
        </>
      }
    >
      <label className="modal-feld">
        <span>
          {typ === 'Rückfrage' ? 'Rückfrage' : typ === 'Antwort' ? 'Antwort' : 'Begründung'}
          {pflicht ? '' : ' (freiwillig)'}
        </span>
        <textarea
          className="modal-textarea"
          value={text}
          autoFocus
          placeholder={
            typ === 'Rückfrage'
              ? 'Was brauchst du, um entscheiden zu können?'
              : typ === 'Antwort'
                ? 'Was fehlt noch zur Entscheidung?'
                : 'Warum diese Entscheidung?'
          }
          onChange={(e) => setText(e.target.value)}
        />
      </label>

      <p className="eh-hinweis">
        {typ === 'Antwort'
          ? fragesteller
            ? `Geht als Mail an ${fragesteller} und bleibt im Verlauf stehen.`
            : 'Bleibt im Verlauf stehen.'
          : eigener
            ? 'Bleibt im Verlauf stehen. Eine Mail an dich selbst entfällt.'
            : `Geht als Mail an ${vorschlag.ersteller} und bleibt im Verlauf stehen.`}
      </p>

      {fehler && <div className="eh-fehler">{fehler}</div>}
    </Modal>
  );
}